import React, { useEffect, useRef } from "react"
import {
	EditorView,
	keymap,
	lineNumbers,
	highlightActiveLine,
	highlightActiveLineGutter,
} from "@codemirror/view"
import { defaultKeymap, history, historyKeymap, indentWithTab } from "@codemirror/commands"
import {
	bracketMatching,
	defaultHighlightStyle,
	foldGutter,
	indentOnInput,
	indentUnit,
	syntaxHighlighting,
} from "@codemirror/language"
import { json } from "@codemirror/lang-json"

/**
 * Minimal CodeMirror 6 JSON editor mounted directly on the core packages.
 *
 * We deliberately avoid the `@uiw/react-codemirror` wrapper: it imports
 * `react/jsx-runtime` as a fully-specified ESM path, which webpack 5 + React 17
 * can't resolve in a production build (CRA5, no webpack override). The core
 * @codemirror/* packages ship clean ESM with proper exports and don't touch
 * React, so they bundle fine.
 *
 * The view is created once on mount; external `value` changes (switching nodes,
 * reset-to-defaults) are diffed against the current doc and dispatched as a
 * replace, which avoids an update loop with the parent's onChange.
 */

const baseExtensions = (onChangeRef) => [
	lineNumbers(),
	highlightActiveLineGutter(),
	highlightActiveLine(),
	history(),
	foldGutter(),
	indentOnInput(),
	indentUnit.of("    "),
	bracketMatching(),
	syntaxHighlighting(defaultHighlightStyle, { fallback: true }),
	json(),
	keymap.of([...defaultKeymap, ...historyKeymap, indentWithTab]),
	EditorView.updateListener.of((update) => {
		if (update.docChanged) onChangeRef.current?.(update.state.doc.toString())
	}),
]

function JsonEditor({ value = "", onChange }) {
	const hostRef = useRef(null)
	const viewRef = useRef(null)
	const onChangeRef = useRef(onChange)

	useEffect(() => {
		onChangeRef.current = onChange
	}, [onChange])

	// Mount once.
	useEffect(() => {
		const view = new EditorView({
			doc: value || "",
			parent: hostRef.current,
			extensions: baseExtensions(onChangeRef),
		})
		viewRef.current = view
		return () => view.destroy()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	// Push external value changes into the editor without clobbering live typing.
	useEffect(() => {
		const view = viewRef.current
		if (!view) return
		const current = view.state.doc.toString()
		if (value != null && value !== current) {
			view.dispatch({ changes: { from: 0, to: current.length, insert: value } })
		}
	}, [value])

	return <div className="ai-flow-json-editor" ref={hostRef} />
}

export default JsonEditor
